import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { getBestContrastColor } from '../../common/color.helper';
import { Button } from '../../components/actions/Button';
import * as Icon from '../../components/icons';
import ColorPicker from '../../components/inputs/ColorPicker';
import InputNumber from '../../components/inputs/InputNumber';
import InputText from '../../components/inputs/InputText';
import TitleSection from '../../components/layout/TitleSection';
import { ActionBar } from '../../components/nav/ActionBar';
import { FlippableStat } from '../../components/stats/FlippableStat';
import { Stat } from '../../components/stats/Stat';
import { StatGroup } from '../../components/stats/StatGroup';
import {
  analyzeGateUsageScheduleCustomThresholds,
  analyzeGateUsageScheduleWaitTimeCriticalThreshold,
  analyzeGateUsageScheduleWaitTimeLateThreshold,
  analyzeGateUsageScheduleWaitTimeWarningThreshold,
} from '../store/analyze.state';
import {
  WaitTimeClassificationModel,
  WaitTimeThreshold,
} from './waitTimeClassificationModel';

// Add default colors array near the top of the component
const defaultThresholdColors = [
  '#FF9F5B', // Orange
  '#FF476E', // Pink
  '#6F8DE8', // Blue
  '#8DC452', // Green
  '#EA5275', // Red
  '#3EC0E8', // Cyan
  '#FF8939', // Dark Orange
  '#F9B428', // Yellow
  '#5FA7E9', // Light Blue
  '#8CC464', // Light Green
];

const ThresholdControls: React.FC = () => {
  const { t } = useTranslation('simulation');
  const [warningThreshold, setWarningThreshold] = useRecoilState(
    analyzeGateUsageScheduleWaitTimeWarningThreshold,
  );
  const [lateThreshold, setLateThreshold] = useRecoilState(
    analyzeGateUsageScheduleWaitTimeLateThreshold,
  );
  const [criticalThreshold, setCriticalThreshold] = useRecoilState(
    analyzeGateUsageScheduleWaitTimeCriticalThreshold,
  );
  const [customThresholds, setCustomThresholds] = useRecoilState(
    analyzeGateUsageScheduleCustomThresholds,
  );

  const [isEditing, setIsEditing] = useState(false);
  const [editingThresholds, setEditingThresholds] = useState<
    WaitTimeThreshold[]
  >([]);

  useEffect(() => {
    if (isEditing) {
      const model = new WaitTimeClassificationModel(
        warningThreshold,
        lateThreshold,
        criticalThreshold,
        customThresholds || undefined,
      );
      // Use deep clone to avoid read-only property issues
      const thresholds = model
        .getAllThresholds()
        .map(t => WaitTimeClassificationModel.deepCloneThreshold(t));

      setEditingThresholds(thresholds);
    } else {
      setEditingThresholds([]);
    }
  }, [
    isEditing,
    customThresholds,
    warningThreshold,
    lateThreshold,
    criticalThreshold,
  ]);

  const handleSave = () => {
    const validThresholds = editingThresholds.filter(
      t => t.maxSeconds !== null && t.maxSeconds >= 0,
    );
    setCustomThresholds(validThresholds);

    const slightlyLate = validThresholds.find(t => t.id === 'slightly-late');
    if (slightlyLate) setWarningThreshold(slightlyLate.maxSeconds);

    const late = validThresholds.find(t => t.id === 'late');
    if (late) setLateThreshold(late.maxSeconds);

    const veryLate = validThresholds.find(t => t.id === 'very-late');
    if (veryLate) setCriticalThreshold(veryLate.maxSeconds);

    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleReset = () => {
    setCustomThresholds(null);
    // Reset legacy atoms to their defaults by creating a fresh model
    const defaultModel = new WaitTimeClassificationModel();
    const defaultThresholds = defaultModel.getAllThresholds();
    const slightlyLate = defaultThresholds.find(t => t.id === 'slightly-late');
    if (slightlyLate) setWarningThreshold(slightlyLate.maxSeconds);
    const late = defaultThresholds.find(t => t.id === 'late');
    if (late) setLateThreshold(late.maxSeconds);
    const veryLate = defaultThresholds.find(t => t.id === 'very-late');
    if (veryLate) setCriticalThreshold(veryLate.maxSeconds);

    setIsEditing(false);
  };

  const updateAndReorderThresholds = (
    thresholdId: string,
    updates: Partial<WaitTimeThreshold>,
  ) => {
    // Create new thresholds array with deep clones and updates
    let newThresholds = editingThresholds.map(t => {
      if (t.id === thresholdId) {
        // Create a deep clone and apply updates
        const cloned = WaitTimeClassificationModel.deepCloneThreshold(t);
        return { ...cloned, ...updates };
      }
      return WaitTimeClassificationModel.deepCloneThreshold(t);
    });

    newThresholds = _.sortBy(newThresholds, 'maxSeconds');

    const finalThresholds = newThresholds.map((threshold, index) => {
      const cloned = WaitTimeClassificationModel.deepCloneThreshold(threshold);
      if (index === 0) {
        return cloned;
      }
      const previousThreshold = newThresholds[index - 1];
      cloned.minSeconds = previousThreshold.maxSeconds;
      return cloned;
    });

    setEditingThresholds(finalThresholds);
  };

  const addThreshold = () => {
    const lastFiniteThreshold = _.findLast(
      editingThresholds,
      t => t.maxSeconds !== Infinity,
    );
    const newMin = lastFiniteThreshold ? lastFiniteThreshold.maxSeconds : 0;

    // Get a default color based on the number of existing thresholds
    const colorIndex = editingThresholds.length % defaultThresholdColors.length;
    const defaultBackgroundColor = defaultThresholdColors[colorIndex];
    const defaultTextColor = getBestContrastColor(defaultBackgroundColor);

    const newThreshold: WaitTimeThreshold = {
      id: `custom-${_.uniqueId()}`,
      label: 'New Threshold',
      description: 'A new wait time threshold',
      minSeconds: newMin,
      maxSeconds: newMin + 60 * 15, // 30 minutes later
      color: defaultTextColor,
      backgroundColor: defaultBackgroundColor,
      classNameTd: '',
      timelineColors: [defaultBackgroundColor, defaultTextColor],
      severity: 'medium',
      actionRequired: 'Review',
    };

    // Deep clone existing thresholds and add the new one
    const clonedThresholds = editingThresholds.map(t =>
      WaitTimeClassificationModel.deepCloneThreshold(t),
    );
    const updatedThresholds = [...clonedThresholds, newThreshold];
    setEditingThresholds(_.sortBy(updatedThresholds, 'maxSeconds'));
  };

  const removeThreshold = (id: string) => {
    const updated = editingThresholds
      .filter(t => t.id !== id)
      .map(t => WaitTimeClassificationModel.deepCloneThreshold(t));
    setEditingThresholds(_.sortBy(updated, 'maxSeconds'));
  };

  const model = new WaitTimeClassificationModel(
    warningThreshold,
    lateThreshold,
    criticalThreshold,
    customThresholds || undefined,
  );

  const displayThresholds = isEditing
    ? editingThresholds
    : model.getAllThresholds();

  return (
    <TitleSection
      collapsible
      inPanelView
      hasAction={
        <div className="flex w-full items-center gap-2">
          <div className="flex-1">{t('Time Thresholds')}</div>
          {!isEditing ? (
            <Button
              buttonType="primary"
              buttonSize="sm"
              onPress={() => setIsEditing(true)}
            >
              {t('Edit')}
            </Button>
          ) : (
            <>
              <Button
                buttonSize="sm"
                onPress={addThreshold}
                buttonType="secondary"
                className="rounded-full p-1"
              >
                <Icon.Plus className="h-4 w-4 fill-current" />
              </Button>
              <Button
                buttonSize="sm"
                onPress={handleReset}
                buttonType="secondary"
                className="rounded-full p-1"
              >
                <Icon.Refresh className="h-4 w-4 fill-current" />
              </Button>
              <Button
                buttonSize="sm"
                onPress={handleCancel}
                buttonType="secondary"
                className="rounded-full p-1"
              >
                <Icon.Close className="h-4 w-4 fill-current" />
              </Button>
              <Button buttonType="primary" buttonSize="sm" onPress={handleSave}>
                <Icon.CircleOk className="h-4 w-4 fill-current" />
                {t`Save`}
              </Button>
            </>
          )}
        </div>
      }
    >
      <StatGroup className={`flex w-full flex-col ${isEditing ? '' : ''}`}>
        {_.sortBy(displayThresholds, 'minSeconds').map(threshold => {
          const isEditable = isEditing;

          const front = (
            <Stat
              title={threshold.label}
              value={`${threshold.maxSeconds === Infinity ? '∞' : Math.round(threshold.maxSeconds / 60)} min`}
              legendColor={[threshold.backgroundColor, threshold.color]}
              className="w-full"
              inPanelMode
              isPreview
              isActionable
              isSelected
              toggleIconOpen={Icon.CircleOk}
              toggleIconClosed={Icon.CircleX}
              // inFilterStat
            ></Stat>
          );

          const back = (
            <Stat
              legendColor={[threshold.backgroundColor, threshold.color]}
              className="bg-menu w-full"
              // isActionable
              // isSelectable
              inPanelMode
              inFilterStat
              isPreview
              classChildren="gap-2 space-y-4"
            >
              <div className="flex flex-col gap-1">
                <ColorPicker
                  color={threshold.backgroundColor}
                  onChange={color =>
                    updateAndReorderThresholds(threshold.id, {
                      backgroundColor: color,
                      color: getBestContrastColor(color),
                    })
                  }
                  className="!h-10 !w-full !rounded-md"
                />
                <InputText
                  value={threshold.label}
                  onChange={value =>
                    updateAndReorderThresholds(threshold.id, { label: value })
                  }
                  className="flex-1 border-none !p-2 text-xs"
                />
              </div>
              <div className="flex items-end">
                <InputNumber
                  title={t('Max Time (minutes)')}
                  value={
                    threshold.maxSeconds === Infinity
                      ? 99999
                      : Math.round(threshold.maxSeconds / 60)
                  }
                  onChange={value =>
                    updateAndReorderThresholds(threshold.id, {
                      maxSeconds:
                        (value || 0) >= 99999 ? Infinity : (value || 0) * 60,
                    })
                  }
                  disabled={threshold.id === 'critically-late'}
                  className="flex-1"
                />
              </div>

              <ActionBar transparent className="flex justify-end py-4">
                <Button
                  onPress={() => removeThreshold(threshold.id)}
                  buttonSize="xs"
                  label={t`Remove`}
                  hasIconAfter
                  buttonIcon={
                    <Icon.CircleX className={`h-5 w-5 fill-current`} />
                  }
                  buttonType="delete"
                  className="rounded"
                />
              </ActionBar>
            </Stat>
          );

          return (
            <div key={threshold.id}>
              <FlippableStat front={front} back={back} isFlipped={isEditable} />
            </div>
          );
        })}
      </StatGroup>
    </TitleSection>
  );
};

export default ThresholdControls;
